import React, { useEffect, useRef, useState } from "react";
import { IconButton } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { marked } from "marked";
import DOMPurify from "dompurify";

import InnoIcons from "@InnoIcons/";
import Button from "InnoUI/components/Button";
import theme from "@theme";
import SC from "./style";

import QuillToolbar, {
  modules,
  formats,
} from "views/ProjectHome/Journal/AddEditJournal/MessageContent/QuillToolbar";

/**
 * Convert Markdown → Safe HTML
 */
const markdownToHtml = (input) => {
  const text = String(input || ""); // ensure string always
  const rawHtml = marked.parse(text);
  return DOMPurify.sanitize(rawHtml);
};

/**
 * ShimmerSkeleton Loader
 */
const ShimmerSkeleton = ({ lines = 6 }) => (
  <div
    style={{
      padding: 12,
      background: "#FFF8F6",
      borderRadius: 8,
      flex: 1,
      overflowY: "auto",
      maxHeight: "580px",
      border: "1px solid #FFA366",
    }}
  >
    {Array.from({ length: lines }).map((_, i) => (
      <div
        key={i}
        style={{
          height: 12,
          borderRadius: 6,
          marginBottom: 12,
          background:
            "linear-gradient(90deg, #e8e8e8 0%, #f5f5f5 50%, #e8e8e8 100%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.2s linear infinite",
        }}
      />
    ))}
  </div>
);

const SummaryPanel = ({
  summaryText = "", // AI markdown OR html
  setSummaryText,
  summaryLoading,
  onToggleCollapse,
  onUseAsNote,
  onClose,
}) => {
  const quillRef = useRef(null);
  const [editMode, setEditMode] = useState(false);

  /**
   * Convert Markdown → HTML ONCE when AI updates
   */
  useEffect(() => {
    if (!summaryText) return;

    // If text is markdown (not HTML), convert it
    const text = String(summaryText || "");
    const isHtml = text.trim().startsWith("<");

    if (!isHtml) {
      const html = markdownToHtml(summaryText);
      setSummaryText(html);
    }
  }, [summaryText, setSummaryText]);

  /**
   * Exit edit mode after loading finishes
   */
  useEffect(() => {
    if (!summaryLoading) {
      setEditMode(false);
    }
  }, [summaryLoading]);

  /**
   * Activate editor on click
   */
  const handleActivateEditor = () => {
    setEditMode(true);
    setTimeout(() => {
      quillRef.current?.getEditor()?.focus();
    }, 50);
  };

  /**
   * Quill change handler (HTML output)
   */
  const handleQuillChange = (html) => {
    setSummaryText(html);
  };

  /**
   * Sanitize HTML before rendering preview
   */
  const hasValidSummary =
    typeof summaryText === "string" && summaryText.trim().length > 0;

  const safeHtml = hasValidSummary
    ? DOMPurify.sanitize(summaryText)
    : "<div style='color:#777;'>No summary generated yet.</div>";

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* HEADER */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          display: "flex",
          justifyContent: "space-between",
          padding: 12,
          backgroundColor: "#FFF9E5",
          borderBottom: "1px solid #EBC850",
          height: 46,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SC.AITextStyle>AI-Generated Summary</SC.AITextStyle>
          <span style={{ fontSize: 12, color: "#666", fontStyle: "italic" }}>
            Click text to edit
          </span>
        </div>

        <IconButton size="small" onClick={onToggleCollapse}>
          <InnoIcons.KeyboardDoubleArrowLeftIcon
            sx={{ fontSize: 28, color: "black" }}
          />
        </IconButton>
      </div>

      {/* CONTENT */}
      <div style={{ padding: 12, flex: 1, minHeight: 0 }}>
        {summaryLoading ? (
          <ShimmerSkeleton lines={20} />
        ) : editMode ? (
          /** EDIT MODE (QUILL HTML) */
          <div
            style={{
              background: "#fff",
              borderRadius: 6,
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <QuillToolbar
              showUndoRedo={false}
              showHeader={false}
              showStrike={false}
              showColor={false}
              showBackground={false}
              showImage={false}
            />

            <div style={{ flex: 1, overflow: "auto" }}>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={String(summaryText || "")}
                onChange={handleQuillChange}
                modules={modules}
                formats={formats}
                style={{ height: "100%" }}
              />
            </div>

            <div style={{ textAlign: "right", paddingTop: 8 }}>
              <InnoIcons.DoneIcon
                onClick={() => setEditMode(false)}
                sx={{
                  color: "green",
                  fontSize: 26,
                  cursor: "pointer",
                }}
              />
            </div>
          </div>
        ) : (
          /** PREVIEW MODE (HTML) */
          <div
            onClick={handleActivateEditor}
            style={{
              background: "#FFF8F6",
              borderRadius: 8,
              padding: 12,
              height: "100%",
              overflowY: "auto",
              border: "1px solid #FFA366",
              cursor: "text",
              fontSize: 13,
            }}
            dangerouslySetInnerHTML={{
              __html:
                safeHtml ||
                "<div style='color:#777;'>No summary generated yet.</div>",
            }}
          />
        )}
      </div>

      {/* FOOTER */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          padding: 12,
          display: "flex",
          justifyContent: "center",
          gap: 12,
          borderTop: "1px solid rgba(0,0,0,0.06)",
          background: "#fff",
        }}
      >
        <Button
          caption="Close"
          onClick={onClose}
          fontSize="12px"
          style={{
            minWidth: 100,
            height: 36,
            background: theme.colors?.cancelButton,
            color: theme.colors?.white,
          }}
        />

        <Button
          caption="Use as Document Summary"
          onClick={onUseAsNote}
          color={theme.colors?.white}
          fontSize="12px"
          style={{
            minWidth: 180,
            height: 36,
            background: theme.gradient?.themeGradient,
            color: theme.colors?.white,
          }}
        />
      </div>
    </div>
  );
};

export default SummaryPanel;
